import {
  ar,
  da,
  de,
  enUS,
  es,
  fr,
  id,
  it,
  ja,
  kk,
  ko,
  mn,
  nb,
  nl,
  pl,
  ptBR,
  ru,
  sv,
  th,
  tr,
  uk,
  uz,
  vi,
  zhCN,
  zhTW,
  Locale,
} from 'date-fns/locale';

import { BaseContent } from './pages/Base';
import { OfflineClassPageType } from './pages/OfflineClass';
import { OfflineDashboardPageType } from './pages/OfflineDashboard';
import { OfflineUnauthorizedPageType } from './pages/OfflineUnauthorized';

export const Languages = [
  'en',
  'pt-br',
  'fil',
  'vi',
  'rw-rw',
  'ja',
  'ko-kr',
  'tr',
  'da',
  'nb',
  'id-id',
  'zh-cn',
  'zh-tw',
  'zh-hk',
  'uk',
  'sv',
  'pl',
  'nl',
  'es-cl', // Spanish chile
  'it',
  'fr',
  'de',
  'th',
  'ar',
  'es', // Spanish
  'es-mx', // Spanish mexico
  'ru',
  'kk-kz', // Kazakh
  'mn-mn', // Mongolian
  'uz-uz', // Uzbek
  'tg-tj', // Tajik
] as const;
export type Language = (typeof Languages)[number];
export const defaultLanguage: Language = 'en';
export const toInstructionLanguage: Partial<Record<Language, string>> = {
  en: 'en',
  'pt-br': 'pt-BR',
  fil: 'fil',
  es: 'es',
  'es-cl': 'es-419',
  'es-mx': 'es-419',
  ru: 'ru',
  'rw-rw': 'rw',
};
// Summaries are AI-written, so Japanese needs no instruction content; the API takes 'jp' for Japanese.
export const toSummaryLanguage: Partial<Record<Language, string>> = {
  ...toInstructionLanguage,
  ja: 'jp',
};

export type ChatbotLanguage = 'en' | 'pt';

export const RTLLanguages: Language[] = ['ar'];

export const toChatbotLanguage = (language: Language): ChatbotLanguage => {
  const primaryLang = language.split('-')[0];
  return (primaryLang as ChatbotLanguage) || 'en';
};

export const dateLocaleMap: Record<Language, Locale> = {
  en: enUS,
  'pt-br': ptBR,
  fil: enUS,
  vi,
  'rw-rw': enUS, // Kinyarwanda - not available in date-fns
  ja,
  'ko-kr': ko,
  tr,
  da,
  nb,
  'id-id': id,
  'zh-cn': zhCN,
  'zh-tw': zhTW,
  'zh-hk': zhTW,
  uk,
  sv,
  pl,
  nl,
  'es-cl': es,
  it,
  fr,
  de,
  th,
  ar,
  es,
  'es-mx': es,
  ru,
  'kk-kz': kk,
  'mn-mn': mn,
  'uz-uz': uz,
  'tg-tj': enUS, // Tajik - not available in date-fns
};

export enum StoryblokPage {
  Base = 'base',
  OfflineDashboard = 'offline_dashboard',
  OfflineClass = 'offline_class',
  OfflineStudent = 'offline_student',
  OfflineUnauthorized = 'offline_unauthorized',
}

export type StoryblokVersion = 'draft' | 'published';

export interface PageTypeMap {
  [StoryblokPage.Base]: BaseContent;
  [StoryblokPage.OfflineDashboard]: BaseContent & OfflineDashboardPageType;
  [StoryblokPage.OfflineClass]: BaseContent & OfflineClassPageType;
  [StoryblokPage.OfflineStudent]: BaseContent; // Add other page types as needed
  [StoryblokPage.OfflineUnauthorized]: BaseContent & OfflineUnauthorizedPageType;
}

// Helper type to get the merged translation type based on pageKey
export type GetTranslationType<T extends StoryblokPage | undefined> = T extends keyof PageTypeMap
  ? PageTypeMap[T]
  : BaseContent;

export interface LanguageOption {
  value: Language;
  label: string;
}
